import { Table } from '@components/shared/Table'
import { ColumnDef } from '@tanstack/react-table'
import CommonPool from 'abis/CommonPool.json'
import { useMarkets, useMarketsInfo } from 'domain/synthetics/markets'
import { ethers } from 'ethers'
import { getProvider } from 'rfx/lib/rpc'
import useWallet from 'rfx/lib/wallets/useWallet'
import useBreakpoints from 'hooks/useBreakpoints'
import { useMemo } from 'react'
import useSWR from 'swr'
import VaultAllocationBars from './VaultAllocationBars'
import Typography from 'components/Common/Typography'
import { getColorGlow, getTokenColor } from 'rfx/lib/utils'
import { keyBy } from 'lodash'
import { TOKENS } from 'config/tokens'
import commonPoolStore from '@store/commonPoolStore'
import { formatPrice } from 'apis/birdeye/helpers'

type VaultAllocationData = {
  address: string
  name: string
  subName: string
  allocationPercentage: string
  marketAddress: string
  allocation?: string
}

export const VaultAllocation = () => {
  const { below } = useBreakpoints()
  const { marketsData } = useMarkets()
  const { tokensData } = useMarketsInfo()
  const { chainId } = useWallet()
  const tokens = keyBy(TOKENS[chainId], 'symbol')
  const selectedVault = commonPoolStore((store) => store.selectedVault)
  const commonPoolAddress = selectedVault.value
  const provider = getProvider(undefined, chainId)
  const contract = new ethers.Contract(
    commonPoolAddress,
    CommonPool.abi,
    provider,
  )

  const { data, isLoading } = useSWR(
    `common-pool-current-allocation-${commonPoolAddress}-${chainId}`,
    {
      fetcher: async () => {
        const res = {
          currentAllocation: null,
          allocation: null,
          sum: null,
        }
        try {
          const currentAllocation = await contract.currentAllocation()
          let sum = 0
          const allocation = currentAllocation._markets.map(
            (item: string, idx: number) => {
              const _allocation = parseFloat(
                (Number(currentAllocation?._balances[idx]) * Math.pow(10, -18))
                  .toFixed(2)
                  .toString(),
              )
              //   convertToUsd(
              //     currentAllocation?._balances[idx],
              //     _token.decimals+USD_DECIMALS,
              //     _token.prices.minPrice,
              //   )
              // )
              sum += _allocation
              return _allocation
            },
          )
          return {
            allocation,
            sum,
            currentAllocation,
          }
        } catch (e) {
          return res
        }
      },
    },
  )

  const formattedData: VaultAllocationData[] = useMemo(
    () =>
      data?.currentAllocation?._markets.map((item: string, idx: number) => {
        const address =
          tokensData && marketsData
            ? tokensData[marketsData[item]?.indexTokenAddress]?.symbol
            : '...'

        const poolName = marketsData
          ? marketsData[item]?.name.split(' ')
          : ['...', '...']

        const allocationPercentage = (
          (data?.allocation[idx] / data?.sum) *
          100
        ).toFixed(2)

        return {
          address,
          name: poolName?.[0],
          subName: poolName?.[1],
          marketAddress: data?.currentAllocation._markets[idx],
          allocationPercentage,
          allocation: data?.allocation,
        }
      }),
    [
      data?.allocation,
      data?.currentAllocation?._markets,
      data?.sum,
      marketsData,
      tokensData,
    ],
  )

  const allocatedPercentage = useMemo(() => {
    if (!formattedData || !formattedData.length) {
      return 0
    }
    return formattedData.reduce(
      (acc, x) => acc + Number(x.allocationPercentage),
      0,
    )
  }, [formattedData])

  const columns: ColumnDef<VaultAllocationData>[] = [
    {
      accessorKey: 'address',
      header: 'Market',
      meta: {
        align: 'left',
      },
      cell: ({ row }) => {
        const data = row.original
        return (
          <div className="flex h-full items-center gap-4 pl-2 lg:pl-0">
            <div
              className="h-4 w-4"
              style={{
                background: getTokenColor(data?.name?.split('/')[0]),
                boxShadow: getColorGlow(
                  getTokenColor(data?.name?.split('/')[0]),
                ),
              }}
            />
            <div className="flex flex-col text-sm font-semibold text-th-fgd-1">
              <div>{data.name}</div>
              <div className="font-medium text-th-fgd-3 opacity-90">
                {data.subName}
              </div>
            </div>
          </div>
        )
      },
    },
    {
      accessorKey: 'Exposure',
      header: () => <div className="w-full text-right uppercase">Exposure</div>,
      meta: {
        align: 'right',
      },
      cell: ({ row }) => {
        const { allocation } = row.original
        return (
          <Typography
            font={'font-number'}
            className="text-base font-semibold text-th-fgd-1 opacity-90"
          >
            ${allocation && allocation[row.index]}
          </Typography>
        )
      },
    },
    {
      accessorKey: 'allocationPercentage',
      header: () => (
        <div className="w-full text-right uppercase">allocation</div>
      ),
      meta: {
        align: 'right',
      },
      cell: ({ row }) => {
        const { allocationPercentage } = row.original
        return (
          <Typography
            font={'font-number'}
            className="text-base font-semibold text-th-fgd-1 opacity-90"
          >
            {`${allocationPercentage}%`}
          </Typography>
        )
      },
    },
  ]

  if (below.lg) {
    return (
      <div className="flex h-full w-full flex-col">
        <div className="flex h-[57px] w-full items-center border-b border-th-input-border text-sm font-semibold lg:px-6">
          <div className="flex w-full flex-col gap-1">
            <span className="text-sm font-medium text-th-fgd-1">Legend</span>
            <VaultAllocationBars tokens={tokens} allocations={formattedData} />
          </div>
        </div>

        <Table
          className="h-full w-full"
          tableClassName="w-full h-full"
          rows={formattedData || []}
          columns={columns}
        />

        {100 - allocatedPercentage !== 0 && (
          <div className="mt-2 flex items-center justify-between border-t border-th-input-border px-4 py-4">
            <div className="flex h-full items-center gap-4">
              <div
                className="h-4 w-4"
                style={{
                  background: '#6C757D',
                }}
              />
              <div className="flex flex-col items-center justify-center text-sm font-semibold text-th-fgd-1">
                <div>UnAllocated</div>
              </div>
            </div>

            <div>{formatPrice(100 - allocatedPercentage, 2)}%</div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      <div className="flex h-auto w-full flex-col items-center gap-4 text-sm font-semibold">
        <div className="mt-4 w-full text-sm font-medium leading-tight text-th-fgd-1 lg:text-base">
          <span className="text-th-active">SLV</span> automatically rebalances
          liquidity to the best performing markets with
          <span className="text-th-active"> 0 impermanent loss.</span>
        </div>
        <div className="flex w-full flex-col gap-1">
          <span>Legend</span>
          <VaultAllocationBars tokens={tokens} allocations={formattedData} />
        </div>
      </div>

      <div className="h-[300px] max-w-full">
        <Table
          rows={formattedData || []}
          columns={columns}
          noBorder={true}
          fetching={isLoading}
          theadClassName="bg-patek-neutral-celestial-grey-950"
        />
      </div>
    </div>
  )
}
